import { toast } from 'react-toastify'
import { getErrorMessage } from '../../helpers'
import { BaseApiResponse, QueryProps } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import type {
  CreateListMenuBody,
  ListMenuResponse,
  ListMenusResponse,
  UpdateListMenuBody,
} from './listMenu.types'

export const listMenuApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    fetchListMenus: builder.query<
      ListMenusResponse,
      QueryProps & { fulfilmentCentreId?: string }
    >({
      query: (params) => ({
        url: `v1/list-menu`,
        method: 'GET',
        params,
      }),
      providesTags: [{ type: 'LIST_MENU', id: 'LIST' }],
    }),

    fetchListMenu: builder.query<ListMenuResponse, string>({
      query: (listMenuId) => ({
        url: `v1/list-menu/${listMenuId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'LIST_MENU', id: arg }],
    }),

    reorderListMenuArticles: builder.mutation<
      BaseApiResponse,
      { listMenuId: string; articleIds: string[] }
    >({
      query: ({ listMenuId, articleIds }) => ({
        url: `v1/list-menu/${listMenuId}/reorder-articles`,
        method: 'PATCH',
        body: { articleIds },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST_MENU', id: arg.listMenuId },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    reorderListMenus: builder.mutation<
      BaseApiResponse,
      { listMenuIds: string[] }
    >({
      query: (body) => ({
        url: `v1/list-menu/reorder`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'LIST_MENU', id: 'LIST' }],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    reorderListMenuItems: builder.mutation<
      BaseApiResponse,
      { listMenuId: string; itemIds: string[] }
    >({
      query: ({ listMenuId, itemIds }) => ({
        url: `v1/list-menu/${listMenuId}/reorder-items`,
        method: 'PATCH',
        body: { itemIds },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST_MENU', id: arg.listMenuId },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    updateListMenu: builder.mutation<
      ListMenuResponse,
      { id: string; body: UpdateListMenuBody }
    >({
      query: ({ id, body }) => ({
        url: `v1/list-menu/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LIST_MENU', id: arg.id },
        { type: 'LIST_MENU', id: 'LIST' },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    createListMenu: builder.mutation<ListMenuResponse, CreateListMenuBody>({
      query: (body) => ({
        url: `v1/list-menu`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'LIST_MENU', id: 'LIST' }],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useFetchListMenusQuery,
  useLazyFetchListMenusQuery,
  useFetchListMenuQuery,
  useLazyFetchListMenuQuery,
  useReorderListMenuArticlesMutation,
  useReorderListMenusMutation,
  useReorderListMenuItemsMutation,
  useUpdateListMenuMutation,
  useCreateListMenuMutation,
} = listMenuApiSlice
